/**
 * 換算設定の画面の状態と操作（プロダクト仕様書「画面構成と画面遷移」「画面上の振る舞い」）。
 *
 * 一覧と値は、選択・「追加」「編集」・保存と削除の後・「再読み込み」のときだけ取得し、前に取得したものを使い回さない。
 * 編集モード中に入力内容を上書きしないよう、自動の再取得（失敗時の再試行など）は行わない。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useAuthHeader } from '@/hooks/useAuth';
import * as api from '@/lib/api/credit-conversion-settings';
import type {
  ConsumptionItemOption,
  EntryList,
  EntryValue,
  Failure,
} from '@/lib/api/credit-conversion-settings';
import {
  defaultItemKey,
  findItem,
  type SettingItem,
  type SettingItemKey,
} from '@/lib/credit-conversion-settings/tables';

import {
  defaultParts,
  errorsFromViolations,
  hasErrors,
  initialParts,
  requestTables,
  validateForSave,
} from './editorModel';
import {
  editorReducer,
  isDirty,
  openEditor,
  type EditorAction,
  type EditorState,
} from './editorReducer';

export type Load<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: T }
  | { status: 'error'; message: string };

/**
 * 保存・削除・取得の失敗の表示。`reload` なら「再読み込み」ボタンを置く。
 * `unknown` は保存・削除できたかが分からないとき。入力を変えられなくし、同じ内容のままの再度の保存・削除と、
 * 「再読み込み」で入力内容を破棄して一覧から取得し直すことだけをできるようにする
 */
export interface ActionFailure {
  message: string;
  reload: boolean;
  unknown?: boolean;
}

export type Confirmation = 'save' | 'delete' | 'discard';

export const VALUE_GONE =
  '選択した適用開始日の値はなくなりました。削除されたか、適用開始日が変更された可能性があります。';
export const COPY_SOURCE_GONE =
  '「追加」でコピーする、最も新しい適用開始日の値がなくなりました。削除されたか、適用開始日が変更された可能性があります。もう一度「追加」を押してください。';
export const NOT_EDITABLE = 'この値は適用が始まったため、編集できません。';
/** 保存・削除できたかが分からない旨を表示した後、失敗の表示がないときに示す */
export const FROZEN_NOTICE =
  '保存・削除できたかが分からないため、入力は変えられません。同じ内容のまま、もう一度保存・削除するか、「再読み込み」を押して、入力内容を破棄して取得し直してください。';

/** 失敗の理由として括弧に入れる文。文末の「。」は、括弧の後に付ける「。」と重なるので外す */
function reasonOf(f: Failure): string {
  return f.message.replace(/。$/, '');
}

export function fetchFailureMessage(f: Failure): string {
  switch (f.code) {
    case 'CREDIT_DB_UNAVAILABLE':
      return 'credit DBに接続できませんでした。少し待ってから再読み込みしてください。';
    case 'NETWORK':
      return `サーバーに接続できませんでした（${reasonOf(f)}）。`;
    default:
      return `取得できませんでした（${reasonOf(f)}）。`;
  }
}

export function actionFailure(verb: '保存' | '削除', f: Failure): ActionFailure {
  const reload = '「再読み込み」を押して、取得し直してください。';
  switch (f.code) {
    case 'VALUE_CHANGED':
      return {
        message: `値を取得した後に、この値がほかの保存や削除で変わったか、なくなったため、${verb}しませんでした。${reload}`,
        reload: true,
      };
    case 'NOT_SCHEDULED':
      return {
        message: `この値は適用が始まったため、${verb}しませんでした。${reload}`,
        reload: true,
      };
    case 'DATE_CHANGED':
      return {
        message: `${verb}の途中で日付が変わったため、${verb}しませんでした。${reload}`,
        reload: true,
      };
    case 'USER_EMAIL_MISSING':
      return {
        message: `操作した人のメールアドレスを取得できなかったため、${verb}しませんでした。`,
        reload: false,
      };
    case 'CREDIT_DB_UNAVAILABLE':
      return {
        message: `credit DBに接続できなかったため、${verb}しませんでした。少し待ってから、もう一度${verb}してください。`,
        reload: false,
      };
    case 'SAVE_BUSY':
      return {
        message: `同じ設定項目のほかの保存が終わらなかったため、${verb}しませんでした。もう一度${verb}してください。`,
        reload: false,
      };
    default:
      return {
        message:
          verb === '保存'
            ? `保存できたかが分かりません（${reasonOf(f)}）。入力は変えられません。同じ内容のまま、もう一度保存するか、「再読み込み」を押して、入力内容を破棄して取得し直してください。保存できていた場合は、もう一度保存すると、追加では適用開始日の重複のエラー、編集では値が変わった旨のエラーになります（編集内容が元と同じなら保存できます）。`
            : `削除できたかが分かりません（${reasonOf(f)}）。もう一度削除するか、「再読み込み」を押して、取得し直してください。削除できていた場合は、もう一度削除すると、値が変わった旨のエラーになります。`,
        reload: true,
        unknown: true,
      };
  }
}

/** 表示する適用開始日：指定がその設定項目にあればそれ、なければ適用中の値、それもなければ最も新しい値 */
export function pickDate(list: EntryList, desired: string | null): string | null {
  if (desired && list.entries.some((e) => e.effective_dt === desired)) return desired;
  return (
    list.entries.find((e) => e.category === 'active')?.effective_dt ??
    list.entries[0]?.effective_dt ??
    null
  );
}

const defaultItem = findItem(defaultItemKey)!;

function labelsOf(items: ConsumptionItemOption[]): Record<string, string> {
  return Object.fromEntries(items.map((i) => [i.value, i.label]));
}

export function useConversionSettings() {
  const { getAuthHeader } = useAuthHeader();
  const [searchParams, setSearchParams] = useSearchParams();
  // 取得の途中で関数が作り直されても、走っている取得を続けられるよう参照で持つ
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;
  const initialParams = useRef(searchParams);
  // 画面が最後に書いた URL のクエリ。これと違うクエリになったら、画面の外から URL が変わった
  const lastSearch = useRef(searchParams.toString());

  const [item, setItem] = useState<SettingItem>(
    () => findItem(initialParams.current.get('item')) ?? defaultItem,
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [list, setList] = useState<Load<EntryList>>({ status: 'loading' });
  const [value, setValue] = useState<Load<EntryValue>>({ status: 'idle' });
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  // 保存・削除できたかが分からない旨を表示した後。「再読み込み」まで、入力とほかの操作を止める
  const [frozen, setFrozen] = useState(false);
  // 後から始めた取得の結果だけを使う
  const sequence = useRef(0);

  /** 選んだ設定項目と適用開始日を URL に反映する。履歴には積まない */
  const writeParams = (target: SettingItem, date: string | null) => {
    const params: Record<string, string> = date ? { item: target.key, date } : { item: target.key };
    lastSearch.current = new URLSearchParams(params).toString();
    setParamsRef.current(params, { replace: true });
  };

  /**
   * 一覧と値を取得し直して選び直す。`notifyMissing` なら、選んだ適用開始日が一覧になくなっていたことを知らせる
   * （利用者が一覧から選んだときと、保存した後）。URL の適用開始日がないときは、黙って選び直す。
   */
  const load = useCallback(
    async (
      target: SettingItem,
      desired: string | null,
      options: { notifyMissing?: boolean; retried?: boolean } = {},
    ): Promise<void> => {
      const id = ++sequence.current;
      const auth = authRef.current();
      setList({ status: 'loading' });
      setValue({ status: 'loading' });
      let entries: EntryList;
      try {
        entries = await api.getEntries(target.key, auth);
      } catch (e) {
        if (id !== sequence.current) return;
        setList({ status: 'error', message: fetchFailureMessage(api.toFailure(e)) });
        setValue({ status: 'idle' });
        // 「再読み込み」とブラウザのリロードで、選んだ設定項目と適用開始日から取得し直す
        setSelectedDate(desired);
        writeParams(target, desired);
        return;
      }
      if (id !== sequence.current) return;
      setList({ status: 'ok', data: entries });
      if (
        options.notifyMissing &&
        desired &&
        !entries.entries.some((e) => e.effective_dt === desired)
      ) {
        setNotice(VALUE_GONE);
      }
      const chosen = pickDate(entries, desired);
      setSelectedDate(chosen);
      // 画面が選んだ設定項目と適用開始日も URL に反映する
      writeParams(target, chosen);
      if (!chosen) {
        setValue({ status: 'idle' });
        return;
      }
      try {
        const data = await api.getEntry(target.key, chosen, auth);
        if (id === sequence.current) setValue({ status: 'ok', data });
      } catch (e) {
        if (id !== sequence.current) return;
        const f = api.toFailure(e);
        if (f.code === 'NOT_FOUND' && !options.retried) {
          setNotice(VALUE_GONE);
          await load(target, chosen, { retried: true });
          return;
        }
        setValue({ status: 'error', message: fetchFailureMessage(f) });
      }
    },
    [],
  );

  // 最初の表示のときだけ、URL の設定項目と適用開始日から選ぶ
  useEffect(() => {
    void load(item, initialParams.current.get('date'));
  }, []);

  // 画面の外から URL が変わったとき（表示中にサイドバーからこの画面を開き直したときなど）は、URL のとおりに選び直す。
  // 入力を変更した編集モードでは、画面の側で、破棄してよいかを確認してから URL を変える
  useEffect(() => {
    const current = searchParams.toString();
    if (current === lastSearch.current) return;
    lastSearch.current = current;
    const next = findItem(searchParams.get('item')) ?? defaultItem;
    setFrozen(false);
    setEditor(null);
    setConfirmation(null);
    setNotice(null);
    setFailure(null);
    setItem(next);
    void load(next, searchParams.get('date'));
  }, [searchParams, load]);

  const clearMessages = () => {
    setNotice(null);
    setFailure(null);
  };

  const selectItem = (key: SettingItemKey) => {
    const next = findItem(key) ?? defaultItem;
    clearMessages();
    setItem(next);
    void load(next, null);
  };

  const selectDate = (date: string) => {
    clearMessages();
    void load(item, date, { notifyMissing: true });
  };

  /** 「再読み込み」。編集モードなら入力内容を破棄する */
  const reload = () => {
    clearMessages();
    setFrozen(false);
    setEditor(null);
    void load(item, selectedDate);
  };

  const dispatch = (action: EditorAction) =>
    setEditor((current) => {
      if (!current) return current;
      // 保存できたかが分からない後は、入力を変えさせない。エラーの表示とスクロールは続ける
      if (frozen && action.type !== 'showErrors' && action.type !== 'focusedRow') return current;
      return editorReducer(current, action);
    });

  const startCreate = async () => {
    clearMessages();
    setBusy(true);
    const auth = authRef.current();
    try {
      const entries = await api.getEntries(item.key, auth);
      setList({ status: 'ok', data: entries });
      const latest = entries.entries[0];
      const items = entries.consumption_items ?? [];
      const parts = latest
        ? await api
            .getEntry(item.key, latest.effective_dt, auth)
            .then((v) => initialParts(item, v.tables, v.consumption_items ?? items))
        : defaultParts(item, items);
      setEditor(
        openEditor({
          item,
          action: 'create',
          originalDate: null,
          version: null,
          today: entries.today,
          date: '',
          parts,
          consumptionLabels: labelsOf(items),
        }),
      );
    } catch (e) {
      const f = api.toFailure(e);
      if (f.code === 'NOT_FOUND') {
        setNotice(COPY_SOURCE_GONE);
        void load(item, selectedDate);
      } else {
        setFailure({ message: fetchFailureMessage(f), reload: true });
      }
    } finally {
      setBusy(false);
    }
  };

  const startEdit = async () => {
    if (value.status !== 'ok') return;
    const current = value.data;
    clearMessages();
    setBusy(true);
    const auth = authRef.current();
    try {
      const fresh = await api.getEntry(item.key, current.effective_dt, auth);
      setValue({ status: 'ok', data: fresh });
      if (fresh.category !== 'scheduled') {
        setNotice(NOT_EDITABLE);
        // 選択中の適用開始日は変えずに、一覧だけ取得し直す
        const entries = await api.getEntries(item.key, auth);
        setList({ status: 'ok', data: entries });
        return;
      }
      setEditor(
        openEditor({
          item,
          action: 'update',
          originalDate: fresh.effective_dt,
          version: fresh.version,
          today: fresh.today,
          date: fresh.effective_dt,
          parts: initialParts(item, fresh.tables, fresh.consumption_items ?? []),
          consumptionLabels: labelsOf(fresh.consumption_items ?? []),
        }),
      );
    } catch (e) {
      const f = api.toFailure(e);
      if (f.code === 'NOT_FOUND') {
        setNotice(VALUE_GONE);
        void load(item, current.effective_dt);
      } else {
        setFailure({ message: fetchFailureMessage(f), reload: true });
      }
    } finally {
      setBusy(false);
    }
  };

  /** 「保存」の押下。エラーをすべて消してからチェックし、エラーがなければ確認ダイアログを表示する */
  const requestSave = () => {
    if (!editor) return;
    setFailure(null);
    const errors = validateForSave(editor.parts, editor.date, editor.today);
    dispatch({ type: 'showErrors', errors });
    if (!hasErrors(errors)) setConfirmation('save');
  };

  const save = async () => {
    setConfirmation(null);
    if (!editor) return;
    setBusy(true);
    const auth = authRef.current();
    const request = { effective_dt: editor.date, tables: requestTables(editor.parts) };
    try {
      const saved =
        editor.action === 'create'
          ? await api.createEntry(editor.item.key, request, auth)
          : await api.updateEntry(
              editor.item.key,
              editor.originalDate!,
              { ...request, version: editor.version! },
              auth,
            );
      setEditor(null);
      setFrozen(false);
      toast.success('保存しました。');
      await load(editor.item, saved.effective_dt, { notifyMissing: true });
    } catch (e) {
      const f = api.toFailure(e);
      if (f.code === 'VALIDATION_ERROR') {
        dispatch({ type: 'showErrors', errors: errorsFromViolations(editor.parts, f.violations) });
      } else {
        const failed = actionFailure('保存', f);
        if (failed.unknown) setFrozen(true);
        setFailure(failed);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setConfirmation(null);
    if (value.status !== 'ok') return;
    clearMessages();
    setBusy(true);
    try {
      await api.deleteEntry(
        item.key,
        value.data.effective_dt,
        value.data.version,
        authRef.current(),
      );
      setFrozen(false);
      toast.success('削除しました。');
      // 削除した後は、適用中の値（なければ最も新しい値）を表示する
      await load(item, null);
    } catch (e) {
      const failed = actionFailure('削除', api.toFailure(e));
      if (failed.unknown) setFrozen(true);
      setFailure(failed);
    } finally {
      setBusy(false);
    }
  };

  /** 「キャンセル」。入力を変更していれば、破棄してよいかを確認する */
  const cancel = () => {
    if (frozen) return;
    if (editor && isDirty(editor)) {
      setConfirmation('discard');
    } else {
      setEditor(null);
      setFailure(null);
    }
  };

  const discard = () => {
    setConfirmation(null);
    setEditor(null);
    setFailure(null);
  };

  return {
    item,
    selectedDate,
    list,
    value,
    notice,
    failure,
    editor,
    busy,
    frozen,
    confirmation,
    selectItem,
    selectDate,
    reload,
    dispatch,
    startCreate,
    startEdit,
    requestSave,
    save,
    requestDelete: () => setConfirmation('delete'),
    remove,
    cancel,
    discard,
    closeConfirmation: () => setConfirmation(null),
  };
}
